import { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Plus, X } from "lucide-react";

const fmtWhen = (iso) => {
  if (!iso) return "";
  const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`);
  return d.toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short", timeZone: "Australia/Melbourne" });
};

export function Field({ label, children, testid }) {
  return (
    <div className="space-y-1.5" data-testid={testid}>
      <div className="text-2xs font-semibold uppercase tracking-[0.16em] text-zinc-500">{label}</div>
      {children}
    </div>
  );
}

export function Segmented({ value, onChange, options }) {
  return (
    <div className={`grid gap-1.5 ${options.length > 2 ? "grid-cols-1" : "grid-cols-2"}`}>
      {options.map((o) => {
        const on = value === o.value;
        return (
          <button
            key={o.value} type="button" disabled={o.disabled} onClick={() => onChange(o.value)}
            data-testid={o.testid} title={o.title}
            className={`text-left rounded-md border px-3 py-2 transition-colors duration-150 ${
              on ? "border-zinc-950 bg-zinc-950 text-white" : "border-zinc-200 bg-white hover:border-zinc-400"
            } disabled:opacity-40 disabled:cursor-not-allowed`}
          >
            <div className="text-sm font-semibold leading-tight">{o.label}</div>
            {o.hint && <div className={`text-2xs mt-0.5 ${on ? "text-zinc-300" : "text-zinc-500"}`}>{o.hint}</div>}
          </button>
        );
      })}
    </div>
  );
}

export function TemplatePicker({ pf }) {
  const { meta, s, update, ctx, ctxApplies } = pf;
  const row = ctxApplies ? ctx.template : meta.templates.find((t) => t.key === s.templateKey);
  return (
    <Field label="Assessment template">
      <select
        value={s.templateKey} onChange={(e) => update({ templateKey: e.target.value, regions: [] })}
        disabled={s.mode === "completed"} title={s.mode === "completed" ? "Completed records print with this assessment's own modules" : undefined}
        className="w-full h-9 rounded-md border border-zinc-300 bg-white px-2 text-sm disabled:bg-zinc-50 disabled:text-zinc-500"
        data-testid="print-template-select"
      >
        {meta.templates.map((t) => (
          <option key={t.key} value={t.key}>
            {ctx && t.key === ctx.template_key ? `This assessment — ${ctx.template.modules.slice(1).join(" + ") || "Core"}` : t.label}
          </option>
        ))}
      </select>
      {row && (
        <div className="text-2xs text-zinc-500" data-testid="print-template-version">
          Version: {row.version} · {row.field_count} fields
        </div>
      )}
    </Field>
  );
}

export function RegionPicker({ pf }) {
  const { meta, s, update, ctx, ctxApplies } = pf;
  const [region, setRegion] = useState("");
  const [side, setSide] = useState("");
  const host = meta.templates.find((t) => t.key === s.templateKey)?.region_host;
  if (ctxApplies) {
    if (!ctx.regions.length) return null;
    return (
      <Field label="MSK region examinations" testid="print-ctx-regions">
        <div className="text-xs text-zinc-600">
          {ctx.regions.map((r) => `${r.body_region_custom || r.body_region}${r.laterality ? ` (${r.laterality})` : ""}`).join(" · ")}
          <span className="text-zinc-400"> — blank templates only; saved findings aren't copied.</span>
        </div>
      </Field>
    );
  }
  if (!host) return null;
  const add = () => {
    if (!region) return;
    update({ regions: [...s.regions, { body_region: region, laterality: side || null }] });
    setRegion(""); setSide("");
  };
  return (
    <Field label="Add MSK region examinations (optional)">
      <div className="flex gap-1.5">
        <select value={region} onChange={(e) => setRegion(e.target.value)} data-testid="print-region-select"
          className="flex-1 min-w-0 h-8 rounded-md border border-zinc-300 bg-white px-1.5 text-xs">
          <option value="">Region…</option>
          {meta.region_options.body_regions.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <select value={side} onChange={(e) => setSide(e.target.value)} data-testid="print-region-side"
          className="w-28 h-8 rounded-md border border-zinc-300 bg-white px-1.5 text-xs">
          <option value="">Side…</option>
          {meta.region_options.laterality.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <Button size="sm" variant="outline" className="h-8 px-2" onClick={add} disabled={!region || s.regions.length >= 8}
          data-testid="print-region-add"><Plus className="h-3.5 w-3.5" /></Button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {s.regions.map((r, i) => (
          <span key={i} className="inline-flex items-center gap-1 text-xs rounded-full border border-zinc-300 px-2 py-0.5"
            data-testid={`print-region-chip-${i}`}>
            {r.body_region}{r.laterality ? ` (${r.laterality})` : ""}
            <button type="button" onClick={() => update({ regions: s.regions.filter((_, j) => j !== i) })}
              data-testid={`print-region-remove-${i}`} className="text-zinc-400 hover:text-zinc-900">
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>
    </Field>
  );
}

export function PatientPicker({ pf, clientName }) {
  const { s, update, email, ctx, appointments, identifiers } = pf;
  if (!email) return null;
  const name = ctx?.client_name || clientName || email;
  return (
    <Field label="Patient" testid="print-patient-block">
      <div className="text-sm text-zinc-900 font-medium">{name} <span className="text-zinc-500 font-normal">· {email}</span></div>
      {s.mode === "blank" && (
        <div className="flex items-start gap-2 pt-0.5">
          <Checkbox id="print-include-ids" checked={s.includeIds} onCheckedChange={(v) => update({ includeIds: !!v })}
            data-testid="print-include-ids" />
          <Label htmlFor="print-include-ids" className="text-xs font-normal leading-snug text-zinc-700">
            Include patient / appointment details (identifiers only — no clinical answers)
          </Label>
        </div>
      )}
      {identifiers && (
        <select value={s.appointmentId} onChange={(e) => update({ appointmentId: e.target.value })}
          className="w-full h-8 rounded-md border border-zinc-300 bg-white px-1.5 text-xs" data-testid="print-appointment-select">
          <option value="">No appointment details</option>
          {appointments.map((a) => (
            <option key={a.id} value={String(a.id)}>{fmtWhen(a.datetime)} · {a.type || "Appointment"}</option>
          ))}
        </select>
      )}
    </Field>
  );
}

export function SourcePicker({ pf }) {
  const { s, update, sources } = pf;
  if (s.mode !== "onboarding_prefill") return null;
  if (!sources) return <Field label="Onboarding source"><div className="text-xs text-zinc-500">Loading forms…</div></Field>;
  return (
    <Field label="Onboarding source" testid="print-source-list">
      {sources.needs_choice && !s.sourceId && (
        <div className="text-2xs text-amber-800" data-testid="print-source-needs-choice">
          More than one eligible form — choose the one to use.
        </div>
      )}
      <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
        {sources.sources.map((x) => (
          <button key={x.id} type="button" disabled={!x.eligible} onClick={() => update({ sourceId: x.id })}
            data-testid={`print-source-${x.id}`}
            className={`w-full text-left rounded-md border px-2.5 py-1.5 transition-colors duration-150 ${
              s.sourceId === x.id ? "border-zinc-950 ring-1 ring-zinc-950" : "border-zinc-200 hover:border-zinc-400"
            } disabled:opacity-50 disabled:cursor-not-allowed`}>
            <div className="text-xs font-semibold text-zinc-900">
              {x.template_name} {x.template_version && <span className="font-normal text-zinc-500">v{x.template_version}</span>}
            </div>
            <div className="text-2xs text-zinc-500">
              Submitted {fmtWhen(x.submitted_at)}
              {x.link_kind && x.eligible && <span className="ml-1 text-emerald-700">· linked to this {x.link_kind}</span>}
            </div>
            {!x.eligible && <div className="text-2xs text-zinc-500 italic">{x.reason}</div>}
          </button>
        ))}
      </div>
    </Field>
  );
}

export function ResultPicker({ pf }) {
  const { s, update, results } = pf;
  if (s.mode !== "onboarding_prefill" || !results?.applicable) return null;
  const card = (on) => `w-full text-left rounded-md border px-2.5 py-1.5 transition-colors duration-150 ${
    on ? "border-zinc-950 ring-1 ring-zinc-950" : "border-zinc-200 hover:border-zinc-400"
  } disabled:opacity-50 disabled:cursor-not-allowed`;
  return (
    <Field label="Include scored result (optional)" testid="print-result-list">
      <div className="text-2xs text-zinc-500">Printed with an [R] mark and its date — kept separate from onboarding answers.</div>
      <div className="space-y-1.5 max-h-44 overflow-y-auto pr-1">
        <button type="button" onClick={() => update({ resultId: "" })} className={card(!s.resultId)} data-testid="print-result-none">
          <div className="text-xs font-semibold text-zinc-900">No scored result</div>
          <div className="text-2xs text-zinc-500">Score fields stay blank</div>
        </button>
        {results.results.length === 0 && (
          <div className="text-2xs text-zinc-500" data-testid="print-result-empty">No stored PDQ-39 / KOOS / HOOS results for this client.</div>
        )}
        {results.results.map((x) => (
          <button key={x.id} type="button" disabled={!x.eligible} onClick={() => update({ resultId: x.id })}
            className={card(s.resultId === x.id)} data-testid={`print-result-${x.id}`}>
            <div className="text-xs font-semibold text-zinc-900">
              {x.instrument} {x.version && <span className="font-normal text-zinc-500">v{x.version}</span>}
              <span className="font-normal"> · {x.summary}</span>
            </div>
            <div className="text-2xs text-zinc-500">
              Dated {fmtWhen(x.date)}{x.answered ? ` · ${x.answered}` : ""}{x.recorded_by ? ` · by ${x.recorded_by}` : ""}
            </div>
            {!x.eligible && <div className="text-2xs text-zinc-500 italic">{x.reason}</div>}
          </button>
        ))}
      </div>
    </Field>
  );
}
